import { useVisitForm } from "@/components/onboarding/VisitFormContext";
import { useContactForm } from "@/components/onboarding/ContactFormContext";
import { useDossier } from "@/components/onboarding/DossierGate";
import { openCookiePreferences } from "@/lib/cookieConsent";


import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import wordmarkBlack from "@/assets/techtail-wordmark-black.png.asset.json";
import { withLang } from "@/lib/langPath";

const PixelArrow = () => (
  <svg viewBox="0 0 12 12" className="h-3.5 w-3.5" fill="currentColor" shapeRendering="crispEdges">
    <rect x="1" y="5" width="7" height="2" />
    <rect x="7" y="3" width="2" height="1" />
    <rect x="7" y="8" width="2" height="1" />
    <rect x="9" y="5" width="2" height="2" />
  </svg>
);

const buildVerticals = (t: (k: string) => string) => [
  { label: t("sChrome.footer.retailLabel"), eyebrow: t("sChrome.footer.explore"), href: "/retail", color: "#EFC4BC" },
  { label: t("sChrome.footer.industrialLabel"), eyebrow: t("sChrome.footer.discover"), href: "/industrial", color: "#EDE0B8" },
  { label: t("sChrome.footer.medicalLabel"), eyebrow: t("sChrome.footer.meet"), href: "/medical", color: "#BFE3CE" },

  { label: t("sChrome.footer.contactLabel"), eyebrow: t("sChrome.footer.talkTo"), href: "contact", color: null },
];

const Footer = () => {
  const { open: openVisit } = useVisitForm();
  const { open: openContact } = useContactForm();
  const navigate = useNavigate();
  const location = useLocation();

  const goToSection = (hash: string) => {
    const id = hash.replace("/#", "");
    if (location.pathname === "/") {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(withLang("/"));
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 400);
    }
  };
  const { t } = useTranslation();
  const verticals = buildVerticals(t);

  const [hoverColor, setHoverColor] = useState<string | null>(null);
  const { openDossier } = useDossier();




  return (
    <footer
      id="site-footer"
      data-header-theme="blue"
      className="bg-brand-blue text-black transition-colors duration-500"
      style={hoverColor ? { backgroundColor: hoverColor } : undefined}
    >
      <div className="container mx-auto px-4 md:px-6 pt-16 pb-8">
        {/* Vertical cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {verticals.map((v) => {
            const isContact = v.href === "contact";
            const cardClass = `group relative flex flex-col justify-between text-left ${
              isContact ? "h-20 md:h-24 self-end p-4" : "h-40 md:h-48 p-5"
            } bg-black/10 hover:bg-black/80 transition-colors duration-300`;
            const inner = (
              <>
              <span className="font-dot text-[10px] md:text-xs uppercase tracking-widest text-black/60 group-hover:text-white/70 transition-colors">
                {v.eyebrow}
              </span>
              <span className={`flex items-end justify-between ${isContact ? "text-lg md:text-xl" : "text-2xl md:text-3xl"} text-black group-hover:text-white transition-colors`}>
                {v.label}
                <span className="opacity-0 group-hover:opacity-100 transition-opacity"><PixelArrow /></span>
              </span>
              </>
            );


            if (v.href === "contact") {
              return (
                <button
                  key={v.label}
                  type="button"
                  onClick={openContact}
                  onMouseEnter={() => setHoverColor(v.color)}
                  onMouseLeave={() => setHoverColor(null)}
                  className={cardClass}
                >
                  {inner}
                </button>
              );
            }

            if (v.href.startsWith("/#")) {
              return (
                <button
                  key={v.label}
                  type="button"
                  onClick={() => goToSection(v.href)}
                  onMouseEnter={() => setHoverColor(v.color)}
                  onMouseLeave={() => setHoverColor(null)}
                  className={cardClass}
                >
                  {inner}
                </button>
              );
            }

            return (
              <Link
                key={v.label}
                to={withLang(v.href)}
                onMouseEnter={() => setHoverColor(v.color)}
                onMouseLeave={() => setHoverColor(null)}
                className={cardClass}
              >
                {inner}
              </Link>
            );
          })}
        </div>





        {/* Links */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <Link to={withLang("/")} className="inline-block" aria-label="TECHTAIL">
              <img src={wordmarkBlack.url} alt="TECHTAIL" className="h-8 md:h-10 w-auto" />
            </Link>
            <p className="mt-6 text-xl md:text-3xl leading-tight text-black/80 max-w-xl">
              {t("sChrome.footer.tagline")}
            </p>
          </div>

          <div className="md:col-span-2">
            <nav className="flex flex-col gap-2 text-sm">
              <button type="button" onClick={() => goToSection("/#tecnologia")} className="text-left hover:opacity-60 transition-opacity">{t("sChrome.footer.techLink")}</button>
              <Link to={withLang("/compania")} className="hover:opacity-60 transition-opacity">{t("sChrome.footer.companyLink")}</Link>
              <Link to={withLang("/recursos")} className="hover:opacity-60 transition-opacity">{t("sContent.recursos.label")}</Link>
              <Link to={withLang("/casos")} className="hover:opacity-60 transition-opacity">{t("sContent.casos.label")}</Link>
              <button type="button" onClick={() => goToSection("/#barcelona")} className="text-left hover:opacity-60 transition-opacity">Barcelona</button>
            </nav>
          </div>

          <div className="md:col-span-2">
            <nav className="flex flex-col gap-2 text-sm">
              <Link to={withLang("/privacidad")} className="hover:opacity-60 transition-opacity">{t('common.privacy')}</Link>
              <Link to={withLang("/legal")} className="hover:opacity-60 transition-opacity">{t('common.legal')}</Link>
              <Link to={withLang("/cookies")} className="hover:opacity-60 transition-opacity">{t('common.cookies')}</Link>
              <Link to={withLang("/canal-etico")} className="hover:opacity-60 transition-opacity">{t("sChrome.footer.ethicsChannel")}</Link>
              <button type="button" onClick={openCookiePreferences} className="text-left hover:opacity-60 transition-opacity">{t("sChrome.cookieConsent.manage")}</button>

            </nav>
          </div>

          <div className="md:col-span-3 md:text-right">
            <a href="mailto:hola@techtail.es" className="inline-block text-base md:text-lg underline underline-offset-4 hover:opacity-60 transition-opacity">
              hola@techtail.es
            </a>
            <div>
              <a
                href="https://www.linkedin.com/company/techtail-company/posts/?feedView=all"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-base md:text-lg underline underline-offset-4 hover:opacity-60 transition-opacity"
              >
                {t("sChrome.footer.linkedin")}
              </a>
            </div>

            <div className="mt-8">
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-blue">
                {t("sChrome.footer.hq")}
              </p>
              <p className="mt-2 whitespace-nowrap text-sm md:text-base text-black/80">
                Carrer Escultor Llimona, 9, 08328 Alella, Barcelona
              </p>

            </div>
          </div>
        </div>




      </div>
    </footer>
  );
};

export default Footer;
